import React, { ChangeEvent, useRef, useState } from 'react';
import { GradientText, GradientTextAction, InputLabel, Poppins, Spacer, StyledAvatar } from 'src/common';
import Button from 'src/components/form/Button';
import styled from 'styled-components';
import { getProfileCredentials } from 'src/utils/auth';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ConfirmDeleteModal } from 'src/components/modals';
import { TabWrap } from 'src/components/TabsMenu';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import {
  OrganisationQuery,
  UpsertOrganisationMutationVariables,
  useArchiveOrganisationMutation,
  useUpsertOrganisationMutation,
} from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';
import {
  OrganisationTypeSelect,
  BusinessTypeSelect,
  PlanTypeSelect,
  ServiceModelSelect,
  BillingTypeSelect,
  EnvironmentTypeSelect,
} from 'src/components/select';
import { getDiff } from 'src/utils/getDiff';

const Div = styled(TabWrap)`
  background-color: ${({ theme }) => theme.colors.white};

  .prof-img {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    &__input {
      display: none;
    }
  }

  form {
    .contents {
      display: grid;
      grid-column-gap: 50px;
      grid-row-gap: 20px;
      grid-template-columns: 1fr 1fr;
    }

    .controls {
      display: flex;
      align-items: center;
      justify-content: space-between;
      grid-gap: 13px;
      gap: 13px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    form {
      .contents {
        grid-template-columns: 1fr;
        grid-gap: 20px;
      }
    }
  }
`;

interface DetailsProps {
  organisation: NonNullable<OrganisationQuery['organisation']>;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Required'),
  primaryContact: Yup.string().email('Invalid email address').required('Required'),
  billingContact: Yup.string().email('Invalid email address').nullable(),
});

export const Details: React.FC<DetailsProps> = ({ organisation }) => {
  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);
  const [archiveWarningModalOpen, setArchiveWarningModalOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const { refetchQuery } = useCache();
  const refetch = () => refetchQuery({ include: ['organisation', 'flatOrganisations'] });

  const [update, { error }] = useUpsertOrganisationMutation();

  const [archiveOrganisation, { loading: isArchiving }] = useArchiveOrganisationMutation({
    onCompleted: () => refetch(),
  });

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        setSelectedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const initialValues = {
    name: organisation.name,
    primaryContact: organisation.primaryContact,
    billingContact: organisation.billingContact,
    domain: organisation.domain,
    organisationType: organisation.organisationType,
    businessType: organisation.businessType,
    planType: organisation.planType,
    billingType: organisation.billingType,
    serviceModel: organisation.serviceModel,
    environmentType: organisation.environmentType,
  } satisfies UpsertOrganisationMutationVariables as UpsertOrganisationMutationVariables;

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    handleSubmit,
    isValid,
    isSubmitting,
    dirty,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    validationSchema,
    initialValues,
    onSubmit: async (values) => {
      const diff = getDiff(initialValues, values);
      // console.log('====UPDATE ORGANISATION====', { organisationId: organisation.id, ...diff });
      await update({ variables: { organisationId: organisation.id, ...diff } });
      await refetch();
    },
    enableReinitialize: true,
  });

  const canEditSettings = organisation.actionsOnThisOrganisation?.canEditSettings;

  const selectedSettingsOption = organisation.actionsOnThisOrganisation?.settingsOptions.find(
    (o) => o.organisationType === values.organisationType,
  );

  // console.log('==selectedSettingsOption==', selectedSettingsOption);
  // console.log('==values==', values);
  // console.log('==organisation===', organisation);
  // console.log('---');

  return (
    <>
      {archiveWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={() => {
              archiveOrganisation({ variables: { id: organisation.id } });
              setArchiveWarningModalOpen(false);
            }}
            onClose={() => setArchiveWarningModalOpen(false)}
            confirmText="ARCHIVE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to archive{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {organisation.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      <Div>
        <GradientText>DETAILS</GradientText>
        <Spacer $px={32} />
        <div className="prof-img">
          <StyledAvatar src={selectedImage}>{getProfileCredentials(organisation.name)}</StyledAvatar>
          <GradientTextAction onClick={() => inputRef.current?.click()} px={14} weight={500}>
            Update Photo
          </GradientTextAction>
          <input className="prof-img__input" ref={inputRef} type="file" onChange={handleImageChange} accept="image/*" />
        </div>

        <Spacer $px={32} />
        <form onSubmit={handleSubmit}>
          <div className="contents">
            {/* Row 1 */}
            <div>
              <InputLabel>NAME *</InputLabel>
              <AutoGrowingInput
                id="name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
                fullWidth
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
            </div>

            <div>
              <InputLabel>ORGANISATION EMAIL DOMAIN</InputLabel>
              <AutoGrowingInput value={organisation.domain?.replace(/^@/, '')} fullWidth disabled />
            </div>

            {/* Row 2 */}
            <div>
              <InputLabel>PRIMARY CONTACT EMAIL ADDRESS *</InputLabel>
              <AutoGrowingInput
                id="primaryContact"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.primaryContact}
                type="email"
                fullWidth
                error={touched.primaryContact && Boolean(errors.primaryContact)}
                helperText={touched.primaryContact && errors.primaryContact}
              />
            </div>

            <div>
              <InputLabel>BILLING CONTACT EMAIL ADDRESS (OPTIONAL)</InputLabel>
              <AutoGrowingInput
                id="billingContact"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.billingContact}
                type="email"
                fullWidth
                error={touched.billingContact && Boolean(errors.billingContact)}
                helperText={touched.billingContact && errors.billingContact}
              />
            </div>

            {/* Row 3 */}
            <div>
              <InputLabel>PARENT ORGANISATION</InputLabel>
              <AutoGrowingInput value={organisation.name} fullWidth disabled />
            </div>

            <div />

            {/* Row 4 - Full width */}
            <div style={{ gridColumn: '1 / -1' }}>
              <InputLabel tooltip="Changing the Organisation Type will automatically update and reset the related configuration settings below.">
                ORGANISATION TYPE
              </InputLabel>
              <OrganisationTypeSelect
                onChange={(o) => {
                  setFieldValue('organisationType', o.value);

                  setFieldValue('businessType', null);
                  setFieldValue('planType', null);
                  setFieldValue('billingType', null);
                  setFieldValue('serviceModel', null);
                  setFieldValue('environmentType', null);
                }}
                onBlur={() => setFieldTouched('organisationType', true)}
                value={values.organisationType}
                css="width: 100%;"
                error={touched.organisationType && Boolean(errors.organisationType)}
              />
            </div>

            {/* Row 5 */}
            <div>
              <InputLabel>SERVICE MODEL</InputLabel>
              <ServiceModelSelect
                filteredOptions={selectedSettingsOption?.serviceModel?.map((o) => o!)}
                onChange={(o) => setFieldValue('serviceModel', o.value)}
                onBlur={() => setFieldTouched('serviceModel', true)}
                value={values.serviceModel}
                css="width: 100%;"
                error={touched.serviceModel && Boolean(errors.serviceModel)}
              />
            </div>

            <div>
              <InputLabel>BUSINESS TYPE</InputLabel>
              <BusinessTypeSelect
                filteredOptions={selectedSettingsOption?.businessType?.map((o) => o!)}
                onChange={(o) => setFieldValue('businessType', o.value)}
                onBlur={() => setFieldTouched('businessType', true)}
                value={values.businessType}
                css="width: 100%;"
                error={touched.businessType && Boolean(errors.businessType)}
              />
            </div>

            {/* Row 6 */}
            <div>
              <InputLabel>BILLING TYPE</InputLabel>
              <BillingTypeSelect
                filteredOptions={selectedSettingsOption?.billingType?.map((o) => o!)}
                onChange={(o) => setFieldValue('billingType', o.value)}
                onBlur={() => setFieldTouched('billingType', true)}
                value={values.billingType}
                css="width: 100%;"
                error={touched.billingType && Boolean(errors.billingType)}
              />
            </div>

            <div>
              <InputLabel>PRICING PLAN</InputLabel>
              <PlanTypeSelect
                filteredOptions={selectedSettingsOption?.planType?.map((o) => o!)}
                onChange={(o) => setFieldValue('planType', o.value)}
                onBlur={() => setFieldTouched('planType', true)}
                value={values.planType}
                css="width: 100%;"
                error={touched.planType && Boolean(errors.planType)}
              />
            </div>

            {/* Row 7 */}
            <div>
              <InputLabel>ENVIRONMENT TYPE</InputLabel>
              <EnvironmentTypeSelect
                filteredOptions={selectedSettingsOption?.environmentType?.map((o) => o!)}
                onChange={(o) => setFieldValue('environmentType', o.value)}
                onBlur={() => setFieldTouched('environmentType', true)}
                value={values.environmentType}
                css="width: 100%;"
                error={touched.environmentType && Boolean(errors.environmentType)}
              />
            </div>
          </div>

          {error && (
            <>
              <Spacer $px={20} />
              <div className="err">{error.message || 'Something went wrong'}</div>
            </>
          )}
          <Spacer $px={50} />
          <div className="controls">
            {canEditSettings && (
              <>
                <Button
                  primary
                  type="submit"
                  disabled={!isValid || isSubmitting || !dirty}
                  $constWidth={220}
                  onClick={() =>
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Update',
                      tags: ['ORGANISATION'],
                    })
                  }
                >
                  UPDATE
                </Button>
              </>
            )}
            {organisation.actionsOnThisOrganisation?.canArchive && !organisation.isArchived && (
              <Button
                secondary
                type="submit"
                disabled={isArchiving}
                $constWidth={220}
                onClick={() => setArchiveWarningModalOpen(true)}
              >
                ARCHIVE
              </Button>
            )}
          </div>
        </form>
      </Div>
    </>
  );
};
